import { Link } from "react-router-dom";
import { ArrowRight, Bot, Search, Zap, Code, Trophy, AlertTriangle, Sparkles, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChatbotWidget from "../components/ChatbotWidget";

const stats = [
  { k: "2.4B+", v: "Monthly AI search queries" },
  { k: "65%",   v: "Of Gen Z asks AI first" },
  { k: "3.2×",  v: "Avg buyer intent vs Google" },
  { k: "< 1%",  v: "Of small sites are AI-ready" },
];

const platforms = ["ChatGPT", "Claude", "Gemini", "Grok", "Perplexity", "Copilot"];

const codeWins = [
  { icon: Code,    title: "Semantic HTML by default",
    text: "Real <article>, <section>, <nav> — not a soup of <div>s. AI crawlers understand structure first, content second." },
  { icon: Search,  title: "Schema.org markup, everywhere",
    text: "Every service, location, FAQ, and review is wrapped in JSON-LD so AI engines pull rich answers, not guesses." },
  { icon: Bot,     title: "AI-friendly content patterns",
    text: "Q&A blocks, direct first-sentence answers, plain language — the way LLMs prefer to ingest knowledge." },
  { icon: Zap,     title: "Server-rendered, lightning fast",
    text: "AI bots time-budget every page. We render in milliseconds so crawlers always finish — and always come back." },
  { icon: Sparkles,title: "llms.txt + machine-readable feeds",
    text: "We publish a dedicated llms.txt and structured feeds so models know exactly what to recommend you for." },
  { icon: Trophy,  title: "Citation-worthy authority",
    text: "Original copy, real numbers, and proper attribution — the things AI engines weight when picking a source." },
];

export default function FutureOfAISearch() {
  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full -z-10 opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.32), transparent 70%)" }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-14">
          <p className="label-eyebrow">The Future of Search</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-4 font-medium leading-[1.05] max-w-5xl">
            The small business <em className="text-[#C5A059] not-italic">leapfrog</em>.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#5C5C5C] max-w-3xl leading-relaxed">
            For the first time in a decade, the way people find businesses is changing. Customers aren't googling — they're asking ChatGPT, Claude, Gemini and Grok. And the dominant brands you've been losing to on Google? Most of them aren't ready for it. That's your opening.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="ai-search-cta-primary" className="btn-gold">Get my site AI-ready <ArrowRight size={16}/></Link>
            <Link to="/#concepts" className="btn-secondary">See example sites</Link>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.map((s) => (
              <div key={s.v} className="rounded-2xl border border-[#EBE8DF] bg-white p-5">
                <p className="font-display text-3xl md:text-4xl tracking-tight text-[#C5A059]">{s.k}</p>
                <p className="mt-2 text-[12px] uppercase tracking-[0.18em] text-[#8B8C89]">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms marquee-style strip */}
      <section className="border-y border-[#EBE8DF] bg-white py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-6 flex-wrap justify-between">
          <p className="label-eyebrow">Optimized for</p>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {platforms.map((p) => (
              <span key={p} className="font-display text-xl text-[#1A1A1A]">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* THE OPPORTUNITY */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">The opportunity</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4 font-medium leading-tight">
              Why this is your shot.
            </h2>
            <p className="mt-6 text-lg text-[#5C5C5C] leading-relaxed">
              Big brands spent ten years winning Google with budgets you can't match. AI search resets that scoreboard. Models recommend businesses based on clarity, structure, and trust signals — not who spent the most on backlinks. Small businesses that move first will be the named recommendation in their category for years.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            <div className="p-6 rounded-2xl border border-[#EBE8DF] bg-white">
              <AlertTriangle size={20} className="text-[#8B8C89]"/>
              <h3 className="font-display text-xl mt-4">Pre-templated sites</h3>
              <ul className="mt-3 space-y-2 text-[14px] text-[#5C5C5C]">
                {["Generic markup AI can't parse","No structured data","Slow page loads bots abandon","Same template as 50,000 others","Invisible in ChatGPT answers"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-[#D1CDC0] shrink-0"/><span>{x}</span></li>
                ))}
              </ul>
            </div>
            <div className="p-6 rounded-2xl border-2 border-[#C5A059] bg-[#FDFBF7]">
              <Sparkles size={20} className="text-[#C5A059]"/>
              <h3 className="font-display text-xl mt-4">A Shumway-built site</h3>
              <ul className="mt-3 space-y-2 text-[14px]">
                {["Custom-coded for AI crawlers","Schema.org on every page","Server-rendered, sub-second loads","Unique design + unique copy","Named in AI answers for your category"].map((x) => (
                  <li key={x} className="flex items-start gap-3"><Check size={14} className="text-[#C5A059] mt-1 shrink-0"/><span>{x}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE CODE FOR AI — dark section */}
      <section className="py-24 md:py-28 bg-[#0E0E0F] text-[#FDFBF7] relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full opacity-30"
             style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.55), transparent 70%)" }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <p className="label-eyebrow" style={{color:"#C5A059"}}>How we code for AI</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4 font-medium leading-tight max-w-3xl">
            Our backend is the difference. Here's what's <em className="text-[#C5A059] not-italic">under</em> the hood.
          </h2>
          <p className="mt-5 text-lg text-[#D1CDC0] leading-relaxed max-w-2xl">
            Pretty pixels don't matter to AI. What matters is how your site is built — the code, the structure, the speed. Every Shumway site is engineered specifically so AI models can crawl it, understand it, and recommend you.
          </p>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {codeWins.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="p-7 rounded-2xl border border-[#2a2a2c] bg-[#161618] hover:border-[#C5A059] transition-colors">
                  <Icon size={22} className="text-[#C5A059]" strokeWidth={1.6}/>
                  <h3 className="font-display text-2xl mt-5 leading-tight text-[#FDFBF7]">{w.title}</h3>
                  <p className="mt-3 text-[15px] text-[#A8A6A0] leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="label-eyebrow">Don't wait for the giants to catch up</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight font-medium">
            Be the one AI <em className="text-[#C5A059] not-italic">recommends</em>.
          </h2>
          <p className="mt-5 text-lg text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed">
            We'll audit your AI visibility and propose a build that gets your business named in ChatGPT, Claude, Gemini, and Grok answers.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/pricing" className="btn-gold">Request a free AI audit <ArrowRight size={16}/></Link>
            <Link to="/framework" className="btn-secondary">See the Simplicity Sells framework</Link>
          </div>
        </div>
      </section>

      <Footer />
      <ChatbotWidget />
    </div>
  );
}
